import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { ServerResponse, TreetopPropertiesProvider } from 'shared/util-common';

import {
  mapReferenceDtoToReference,
  mapReferenceToDto,
  Reference,
  ReferenceDto,
  GuidelineDto,
  ExtractReferenceResult,
} from '@treetop/shared/model';

type ExtractReferenceResultDto = {
  newReferences?: ReferenceDto[];
  existingReferences?: ReferenceDto[];
};

export type FindReferencesFilter = {
  referenceId?: string;
};

export type FindReferencesOptions = {
  searchTerm?: string;
  filter?: FindReferencesFilter;
};

@Injectable({ providedIn: 'root' })
export class ReferenceHttpService {
  private http = inject(HttpClient);
  private readonly baseUrl = `${TreetopPropertiesProvider.properties.baseUrl}api/references`;

  public addReferences(references: Reference[]): Observable<Reference[]> {
    return this.http
      .post<ReferenceDto[]>(`${this.baseUrl}/bulk`, [...references.map((ref) => mapReferenceToDto(ref))])
      .pipe(map((refs) => refs.map((ref) => mapReferenceDtoToReference(ref))));
  }

  public extractFromFile(file: File): Observable<ExtractReferenceResult> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post<ExtractReferenceResultDto>(`${this.baseUrl}/extract-from-file`, formData).pipe(
      map((result) => ({
        newReferences: result.newReferences.map((ref) => mapReferenceDtoToReference(ref)),
        existingReferences: result.existingReferences.map((ref) => mapReferenceDtoToReference(ref)),
      })),
    );
  }

  public deleteReference(referenceId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${referenceId}`);
  }

  public getReferences(
    page: number,
    pageSize: number,
    options?: FindReferencesOptions,
    includeParentOrganizations = false,
    forAllOrganizations = false,
  ): Observable<ServerResponse<ReferenceDto>> {
    let params = new HttpParams()
      .set('page', page)
      .set('itemsPerPage', pageSize)
      .set('includeParentOrganizations', includeParentOrganizations)
      .set('forAllOrganizations', forAllOrganizations);
    if (options?.searchTerm) {
      params = params.set('searchTerm', options.searchTerm);
    }

    if (options?.filter?.referenceId) {
      params = params.set('filter.referenceId', options.filter.referenceId);
    }

    return this.http.get<ServerResponse<ReferenceDto>>(`${this.baseUrl}`, { params });
  }

  public getReference(referenceId: string): Observable<ReferenceDto> {
    return this.http.get<ReferenceDto>(`${this.baseUrl}/${referenceId}`);
  }

  public editReference(reference: ReferenceDto): Observable<ReferenceDto> {
    return this.http.put<ReferenceDto>(`${this.baseUrl}`, reference);
  }

  public extractOrSearch(search: string, includeParentOrganizations = true): Observable<ExtractReferenceResult> {
    if (!search) {
      return of({
        newReferences: [],
        existingReferences: [],
      });
    }

    const params = new HttpParams().set('searchTerm', search).set('includeParentOrganizations', includeParentOrganizations);

    return this.http.get<ExtractReferenceResultDto>(`${this.baseUrl}/search-or-extract`, { params }).pipe(
      map((result) => ({
        newReferences: result.newReferences.map((ref) => mapReferenceDtoToReference(ref)),
        existingReferences: result.existingReferences.map((ref) => mapReferenceDtoToReference(ref)),
      })),
    );
  }

  public getGuidelinesByReference(referenceId: string): Observable<GuidelineDto[]> {
    return this.http.get<GuidelineDto[]>(`${this.baseUrl}/${referenceId}/used-in-guidelines`);
  }
}
